import React from 'react';
import { Tag, Button, Popover, Select, Spin } from 'antd';
import { Plus } from 'lucide-react';
import { useTagsDisplayController } from './tagsDisplay.controller';

const { Option } = Select;

/**
 * Reusable component to display and manage tags for any entity
 * @param {string} modelType - Type of model (User, Client, Store, etc.)
 * @param {string} modelId - ID of the model instance
 * @param {boolean} editable - Whether tags can be added/removed (default: true)
 * @param {function} onTagsChange - Callback when tags change
 * @param {number} max - Tags shown inline; the rest collapse into a "+N" pill (hover lists them all)
 */
function TagsDisplay({ modelType, modelId, editable = true, onTagsChange, max = 3 }) {
    const {
        tags,
        loading,
        availableTags,
        popoverVisible,
        selectedTagId,
        setSelectedTagId,
        adding,
        handleAddClick,
        handleAddTag,
        handleRemoveTag,
        handlePopoverOpenChange
    } = useTagsDisplayController({ modelType, modelId, onTagsChange });

    const addPopoverContent = (
        <div className="w-[250px] space-y-2">
            <Select
                placeholder="Selecciona una etiqueta"
                className="w-full"
                value={selectedTagId}
                onChange={setSelectedTagId}
                showSearch
                optionFilterProp="label"
                notFoundContent={<span className="text-xs text-surface-500">No hay etiquetas disponibles</span>}>
                {availableTags.map((tag) => (
                    <Option key={tag._id} value={tag._id} label={tag.name}>
                        <span className="flex items-center gap-2">
                            <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: tag.color }} />
                            {tag.name}
                        </span>
                    </Option>
                ))}
            </Select>
            <Button type="primary" size="small" onClick={handleAddTag} loading={adding} block disabled={!selectedTagId}>
                Agregar
            </Button>
        </div>
    );

    if (loading) {
        return <Spin size="small" />;
    }

    const renderTag = (tagItem) => (
        <Tag
            key={tagItem._id}
            color={tagItem.tagId.color}
            closable={editable}
            onClose={(e) => {
                e.preventDefault();
                handleRemoveTag(tagItem.tagId._id);
            }}
            className="!m-0">
            {tagItem.tagId.name}
        </Tag>
    );
    const visible = tags.slice(0, max);
    const hidden = tags.slice(max);

    return (
        <div className="flex flex-wrap items-center gap-1">
            {visible.map(renderTag)}
            {hidden.length > 0 && (
                <Popover
                    title={`Etiquetas (${tags.length})`}
                    content={<div className="flex max-w-[320px] flex-wrap gap-1">{hidden.map(renderTag)}</div>}>
                    <Tag className="!m-0 cursor-default tabular-nums">+{hidden.length}</Tag>
                </Popover>
            )}
            {editable && (
                <Popover content={addPopoverContent} title="Agregar Etiqueta" trigger="click" open={popoverVisible} onOpenChange={handlePopoverOpenChange}>
                    <Button type="dashed" size="small" icon={<Plus />} onClick={handleAddClick}>
                        Agregar
                    </Button>
                </Popover>
            )}
        </div>
    );
}

export default TagsDisplay;
